import {
  Box,
  Button,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import MeasureField, { useMeasureFieldUnits } from './MeasureField';
import { Add, Delete, Pets } from '@mui/icons-material';
import useDebounce from 'src/hooks/useDebounce';

const CardFoodType = ({
  handleInputChange,
  isPartialRescue,
  errors,
  recipients,
  item,
  disabledRecipients,
  value,
  onChange,
}) => {
  const getError = (key) => {
    return errors?.foodTypes?.[item.id]?.[key]?.message;
  };

  const { t } = useTranslation('translation');
  const { WEIGHT_UNITS, defaultWeightUnit } = useMeasureFieldUnits();
  const currentValue = value[item.id];
  const assignedRecipients = currentValue?.assignedRecipients ?? [
    {
      teamId: 'unassigned',
      reservedWeight: 0,
    },
  ];

  const unassignedRecipients = assignedRecipients.filter(
    (recipient) => recipient.teamId == 'unassigned'
  );

  const totalAmount = parseFloat(currentValue?.value) ?? 0;
  const currentAmount = assignedRecipients.reduce(
    (acc, curr) => acc + parseFloat(curr.reservedWeight),
    0
  );
  const debouncedCurrentAmount = useDebounce(currentAmount, 500);

  const getShowFillButton = (amount) => {
    return amount < totalAmount && unassignedRecipients.length <= 1;
  };

  const showFillButton = getShowFillButton(currentAmount);
  const showFillButtonDebounced = getShowFillButton(debouncedCurrentAmount);

  const minReservableWeight = currentValue?.minReservableWeight ?? '';
  const maxReservableWeight = currentValue?.maxReservableWeight ?? '';
  const defaultValues = {
    value: 0,
    unit: defaultWeightUnit.value,
    ...(isPartialRescue
      ? {
          minReservableWeight: 0,
          maxReservableWeight: 0,
          assignedRecipients: [
            {
              teamId: 'unassigned',
              reservedWeight: 0,
            },
          ],
        }
      : {}),
  };
  const handleChange = (key, value) => {
    if (currentValue?.disabled) return;
    if (!currentValue) {
      handleInputChange(item.id)({
        ...defaultValues,
        [key]: value,
      });
    } else {
      handleInputChange(item.id)({ ...currentValue, [key]: value });
    }
  };
  return (
    <Stack
      direction={
        isPartialRescue
          ? {
              xs: 'column',
              md: 'row',
            }
          : 'column'
      }
      alignItems={
        isPartialRescue
          ? {
              xs: 'center',
              md: 'start',
            }
          : 'center'
      }
      gap={3}
      sx={{
        py: 4,
        border: (t) => `1px solid ${t.palette.divider}`,
        borderColor: (t) =>
          errors?.foodTypes?.[item.id] ? 'red' : t.palette.divider,
        transition: 'all 0.3s ease',
        borderRadius: '10px',
        position: 'relative',
        px: 3,
        flex: 1,
        width: '100%',
      }}
    >
      <IconButton
        disabled={currentValue?.disabled || Object.keys(value).length === 1}
        sx={{
          position: 'absolute',
          top: 3,
          left: 3,
        }}
        color="secondary"
        onClick={() => {
          const newFoodTypes = { ...value };
          delete newFoodTypes[item.id];
          onChange(newFoodTypes);
        }}
      >
        <Delete fontSize="small" />
      </IconButton>
      <Stack alignItems="center">
        <Box
          component="img"
          src={item.image}
          sx={{
            width: 230,
            height: 130,
            objectFit: 'cover',
            flexShrink: 0,
          }}
        />
        <Typography mt={3} variant="h6">
          {item.title}
        </Typography>
      </Stack>

      <Stack width="100%">
        <MeasureField
          sx={{
            width: '100%',
          }}
          label={t('common.weight')}
          units={WEIGHT_UNITS}
          value={value[item.id]}
          onChange={(val) =>
            handleInputChange(item.id)({
              ...(!currentValue ? defaultValues : currentValue),
              ...val,
            })
          }
        />
        {isPartialRescue ? (
          <Stack mt={3} direction="column" gap={3}>
            <Stack
              direction={{
                xs: 'column',
                md: 'row',
              }}
              justifyContent="space-between"
              gap={3}
            >
              <Stack alignItems="center" direction="row" gap={2}>
                <TextField
                  label={t('donations.min')}
                  size="small"
                  type="number"
                  error={!!getError('minReservableWeight')}
                  helperText={getError('minReservableWeight')}
                  value={minReservableWeight}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) =>
                    handleChange(
                      'minReservableWeight',
                      parseFloat(e.target.value)
                    )
                  }
                  fullWidth
                />

                <TextField
                  label={t('donations.max')}
                  size="small"
                  type="number"
                  error={!!getError('maxReservableWeight')}
                  helperText={getError('maxReservableWeight')}
                  value={maxReservableWeight}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) =>
                    handleChange(
                      'maxReservableWeight',
                      parseFloat(e.target.value)
                    )
                  }
                  fullWidth
                />
              </Stack>

              <Button
                sx={{
                  py: 1,
                  px: 1,
                  flexShrink: 0,
                }}
                endIcon={<Add />}
                variant="text"
                color="inherit"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleChange('assignedRecipients', [
                    ...assignedRecipients,
                    {
                      teamId: 'unassigned',
                      reservedWeight: 0,
                    },
                  ]);
                }}
              >
                {t('donations.assign-recipient')}
              </Button>
            </Stack>

            <Stack direction="column" spacing={1}>
              {assignedRecipients.map((recipient, idx) => {
                const handleRecipientChange = (key, value) => {
                  const newAssignedRecipients = [...assignedRecipients];
                  newAssignedRecipients[idx] = {
                    ...newAssignedRecipients[idx],
                    [key]: value,
                  };
                  handleChange('assignedRecipients', newAssignedRecipients);
                };
                const errorList = Object.values(
                  errors?.foodTypes?.[item.id]?.assignedRecipients?.[idx] || {}
                ).map((error) => error.message);
                return (
                  <Stack key={idx} direction="column">
                    <Stack direction="row" gap={1}>
                      <Stack
                        flex={1}
                        sx={{
                          opacity: recipient.disabled ? 0.5 : 1,
                          pointerEvents: recipient.disabled ? 'none' : 'auto',
                        }}
                        direction="row"
                        spacing={1}
                      >
                        <FormControl
                          error={
                            !!getError(`assignedRecipients[${idx}].teamId`)
                          }
                          fullWidth
                        >
                          <InputLabel
                            sx={{
                              '&.Mui-focused, &.MuiFormLabel-filled': {
                                transform:
                                  'translate(14px, -9px) scale(0.75) !important',
                              },
                              transform: 'translate(14px, 8px) scale(1)',
                            }}
                          >
                            {t('donations.receiver')}
                          </InputLabel>
                          <Select
                            onClick={(e) => e.stopPropagation()}
                            onSelect={(e) => e.stopPropagation()}
                            size="small"
                            value={recipient.teamId}
                            renderValue={(selected) =>
                              recipients.find((r) => r.team_id === selected)
                                ?.name ||
                              (selected == 'unassigned'
                                ? t('donations.unassigned')
                                : '')
                            }
                            disabled={recipient?.disabled}
                            onChange={(e) => {
                              handleRecipientChange('teamId', e.target.value);
                            }}
                            label={t('donations.receiver')}
                          >
                            <MenuItem value="unassigned">
                              {t('donations.unassigned')}
                            </MenuItem>
                            {recipients
                              .filter(
                                (r) =>
                                  !assignedRecipients
                                    .map((ar) => ar.teamId)
                                    .includes(r.team_id)
                              )
                              .map((r, key) => (
                                <MenuItem
                                  disabled={disabledRecipients.includes(
                                    r.team_id
                                  )}
                                  key={key}
                                  value={r.team_id}
                                >
                                  {r.animal_food_rescuer_type ? (
                                    <>
                                      <Pets fontSize="small" sx={{ mr: 1 }} />
                                    </>
                                  ) : null}
                                  {r.name} &nbsp;
                                  <Typography
                                    color="text.secondary"
                                    variant="overlinee"
                                  >
                                    {r.formatted_address}
                                  </Typography>
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>

                        <TextField
                          sx={{
                            width: '150px',
                          }}
                          error={!!getError('reservedWeight')}
                          disabled={recipient?.disabled}
                          onClick={(e) => e.stopPropagation()}
                          value={recipient.reservedWeight}
                          type="number"
                          size="small"
                          label="Weight"
                          onChange={(e) => {
                            handleRecipientChange(
                              'reservedWeight',
                              e.target.value
                            );
                          }}
                        />
                      </Stack>
                      <Button
                        disabled={
                          recipient?.disabled || assignedRecipients.length === 1
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChange(
                            'assignedRecipients',
                            assignedRecipients.filter(
                              (r) => assignedRecipients.indexOf(r) !== idx
                            )
                          );
                        }}
                        color="secondary"
                        sx={{
                          ...(assignedRecipients.length === 1
                            ? { opacity: 0.5 }
                            : {}),
                          px: '5px !important',
                          minWidth: '40px !important',
                          '& .MuiButton-startIcon': {
                            mx: 0,
                          },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        variant="text"
                        startIcon={<Delete />}
                      >
                        {' '}
                      </Button>
                    </Stack>

                    <Stack direction="column" spacing={0.5}>
                      {errorList.map((error, idx) => (
                        <Typography key={idx} color="error" variant="caption">
                          {error}
                        </Typography>
                      ))}
                    </Stack>
                  </Stack>
                );
              })}
              <Collapse in={showFillButtonDebounced}>
                <Button
                  disabled={!showFillButton}
                  onClick={() => {
                    let updates = {
                      ...currentValue,
                    };

                    let finalAmount;
                    const remaining = totalAmount - currentAmount;

                    if (unassignedRecipients.length == 0) {
                      finalAmount = remaining;
                      updates['assignedRecipients'] = [
                        ...assignedRecipients,
                        {
                          teamId: 'unassigned',
                          reservedWeight: remaining,
                        },
                      ];
                    } else {
                      const unassignedRecipientIndex =
                        assignedRecipients.findIndex(
                          (r) => r.teamId == 'unassigned'
                        );

                      const newAssignedRecipients = [...assignedRecipients];
                      const currentUnassignedRecipient =
                        newAssignedRecipients[unassignedRecipientIndex];
                      finalAmount =
                        remaining +
                        parseFloat(currentUnassignedRecipient.reservedWeight);

                      newAssignedRecipients[unassignedRecipientIndex] = {
                        ...currentUnassignedRecipient,
                        reservedWeight: finalAmount,
                      };

                      updates['assignedRecipients'] = newAssignedRecipients;
                    }

                    if (finalAmount > maxReservableWeight) {
                      updates['maxReservableWeight'] = finalAmount;
                    }

                    handleInputChange(item.id)(updates);
                  }}
                  fullWidth
                  variant="outlined"
                  color="primary"
                >
                  {t('donations.fill-the-remaining-amount')}
                </Button>
              </Collapse>

              {getError('assignedRecipients') ? (
                <Typography color="error" variant="caption">
                  {getError('assignedRecipients')}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  );
};

export default CardFoodType;
